import * as React from "react";
import Api from "./Api";
import CollapseCtl from "./input/CollapseCtl";
import ProductsQtyCtl from "./ProductQtyCtl";
import Constants from "./Constants";
import { Guid } from "@testout/testout-commerce/core/Guid";
import { ISalesItem, SalesItem } from "@testout/testout-commerce/models/commerce/SalesItem";
import { SalesBase } from "@testout/testout-commerce/models/commerce/SalesBase";
import { SalesOrder } from "@testout/testout-commerce/models/commerce/SalesOrder";
import { ProductComponent, ComponentType } from "@testout/testout-commerce/models/commerce/ProductComponent";
import { ProductProperty, ProductPropertyKeys } from "@testout/testout-commerce/models/commerce/ProductProperty";
import { Product } from "@testout/testout-commerce/models/commerce/Product";
import { Price } from "@testout/testout-commerce/models/commerce/Price";
import { ItemNumber } from "@testout/testout-commerce/models/commerce/ItemNumber";

interface LineItemCtlProps {
    salesBase: SalesBase;
    childrenCustomers: Object;
    updateOrderOrQuote: (salesBase: SalesBase) => void;
    showError: (message: string) => void;
    checkingTaxes: (inProgress: boolean) => void;
    disabled: boolean;
}
interface LineItemCtlState {
    loaded: boolean;
}

export default class LineItemCtl extends React.PureComponent<LineItemCtlProps, LineItemCtlState> {

    constructor(props: LineItemCtlProps) {
        super(props);
        this.state = { loaded: false };
    }

    componentDidUpdate() {
        if (this.props.salesBase?.items.length > 0)
            this.setState({ loaded: true })
    }

    getMainLineItemsPlusBlank = (): SalesItem[] => {
        let salesItems = this.props.salesBase.items.filter((si: SalesItem) => si.parentItemId === undefined).sort((a, b) => (a.sequence ?? 0) > (b.sequence ?? 1) ? 1 : -1);

        // Add a blank "select a product" item to the bottom of the main line items
        return this.addBlankLineItem(salesItems, undefined);
    }

    getSubLineItemsPlusBlank = (item: SalesItem): SalesItem[] => {
        let salesItems = this.props.salesBase.items.filter((si: SalesItem) => si.parentItemId === item.itemId).sort((a, b) => (a.sequence ?? 0) > (b.sequence ?? 1) ? 1 : -1);
        if (!this.bundleHasQuantityOrOptional(item))
            return salesItems;

        // Add a blank "select a product" item to the bottom of the sub items if this product has quantity sub-items
        return this.addBlankLineItem(salesItems, item.itemId);
    }

    // Add a blank "select a product" item to the bottom of the line items (or sub-items)
    private addBlankLineItem = (salesItems: SalesItem[], parentItemId?: Guid) => {
        if (!this.props.disabled) {
            let newItem = new SalesItem(undefined, this.props.salesBase);
            newItem.itemId = Guid.create();
            newItem.quantity = 1;
            newItem.productNumber = new ItemNumber(Constants.NEW_PRODUCT_LINE);
            newItem.parentItemId = parentItemId;
            newItem.overridePricing = parentItemId !== undefined;   // Override price to be zero if this is sub-item on a site license
            salesItems.push(newItem);
        }
        return salesItems;
    }

    // The product number, quantity or price of a line item has changed. Save these changes to this.state.SalesOrder
    onProductQtyPriceChange = async (value: SalesItem): Promise<void> => {
        if (this.props.salesBase?.items === undefined)
            return;

        let salesBase = this.props.salesBase;
        let checkTaxes = false;

        // Check if this is a new item that has been added
        let index = this.props.salesBase.items.findIndex((item: SalesItem) => item.itemId === value.itemId);
        if (index === -1) {
            // Item is not found so this must be a new item. Set its sequence number by counting number of items and adding one.
            value.sequence = salesBase.items.filter(i => i.parentItemId === value.parentItemId && i.productNumber.value !== Constants.NEW_PRODUCT_LINE).length + 1;

            // Check if the bundle drop down should be open or closed
            if (this.bundleHasQuantityOrOptional(value))
                value.showBundle = true;

            // Set the default customer number for the new product
            value.customerNumber = Constants.MAIN_SCHOOL;

            // If this is not a sub-item being added, go check our tax amount
            if (value.parentItemId === undefined)
                checkTaxes = true;

            // Add it to the sales order item list.
            salesBase.items.push(value);
        } else {
            // If this was not a sub-item change, and the price changed, flag that we need to check if the tax amount is different now
            if (value.parentItemId === undefined &&
                (salesBase.items[index].unitPrice !== value.unitPrice || salesBase.items[index].quantity !== value.quantity))
                checkTaxes = true;

            // Do extra checking if the selected product is changing
            let removeId = "";
            if (salesBase.items[index].productNumber.value !== value.productNumber.value) {
                // If a bundle with quantity components, show the bundle items below it
                if (this.bundleHasQuantityOrOptional(value))
                    value.showBundle = true;

                // If this is a product change from a bundle to another product, remember the parent ID to remove the sub-items that were part of the old bundle
                if (this.bundleHasQuantityOrOptional(salesBase.items[index]))
                    removeId = salesBase.items[index].itemId.toString();
            }

            // Change the line item
            salesBase.items[index] = value;

            // If this is a product change from a quantity-type bundle to another product, remove the sub-items that were part of the now-deleted bundle
            if (removeId !== "")
                salesBase.items = salesBase.items.filter((item: SalesItem) => item.parentItemId !== new Guid(removeId));
        }

        // Update the UI before we calculate taxes so the user can continue
        this.props.updateOrderOrQuote(salesBase);

        // If this is not a USA order, no need to check taxes.
        if ((this.props.salesBase.shippingCustomer?.address?.postal?.countryCode ?? "") !== "USA")
            return;

        if (checkTaxes)
            this.checkTheTaxes();
    }

    private _taxTimer: number | undefined = undefined;
    checkTheTaxes = async () => {
        this.props.checkingTaxes(true);
        if (this._taxTimer)
            window.clearTimeout(this._taxTimer);
        this._taxTimer = window.setTimeout(async () => {
            this._taxTimer = undefined;
            let salesBase = this.props.salesBase;
            try {
                // Use Commerce Server to contact Avalara for the tax amount(s) of the current order
                let taxedOrder = await Api.post<SalesOrder>("/api/orders/gettaxes", salesBase);

                // Copy the tax amounts we got from Avalara
                salesBase.taxSchedule = taxedOrder.taxSchedule;
                salesBase.freightTaxAmount = taxedOrder.freightTaxAmount;
                for (var item of salesBase.items) {
                    let itemId = item.itemId.toString();
                    let taxedItem = taxedOrder.items.filter((taxItem: SalesItem) => taxItem.itemId.toString() === itemId);
                    if (taxedItem !== undefined && taxedItem[0] !== undefined)
                        item.taxAmount = taxedItem[0].taxAmount;
                }
                this.props.updateOrderOrQuote(salesBase);
                this.props.checkingTaxes(false);
            }
            catch (error: any) {
                this.props.showError(`Taxes were not calculated correctly: ${error.message}`);
                this.props.checkingTaxes(false);
            }
        }, 500);
    }

    // Remove a line item (and its sub-items, if they exist)
    deleteLineItem = (itemId: Guid) => {
        let index = this.props.salesBase.items.findIndex((i: SalesItem) => i.itemId === itemId);
        if (index === -1)
            return;
        let parentId = this.props.salesBase.items[index].parentItemId;
        this.props.salesBase.items = this.props.salesBase.items.filter((i: SalesItem) => i.parentItemId !== itemId && i.itemId !== itemId);
        // Redo the sequence of the remaining items
        let sequence = 1;
        for (var item of this.props.salesBase.items.filter(i => i.parentItemId === parentId).sort((a, b) => (a.sequence ?? 1) > (b.sequence ?? 1) ? 1 : -1))
            item.sequence = sequence++;
        this.props.updateOrderOrQuote(this.props.salesBase);
    }

    onBundleChange = (itemId: Guid) => {
        let index = this.props.salesBase.items.findIndex((i: SalesItem) => i.itemId === itemId);
        if (index === -1)
            return;
        this.props.salesBase.items[index].showBundle = !this.props.salesBase.items[index].showBundle;
        this.props.updateOrderOrQuote(this.props.salesBase);
    }

    // Determine if the product selected from the line item's drop down is a bundle
    isBundle = (item: ISalesItem): boolean => {
        return (this.getProductInfoForSalesItem(item)?.components?.length ?? 0) > 0;
    }

    // Method used to get the product details for a line item
    private getProductInfoForSalesItem = (item: ISalesItem): Product | undefined => {
        return this.props.salesBase.pricing?.priceList?.products.find((p: Product) => p.productNumber === item.productNumber?.toString());
    }

    // Determine if a line item's product is a bundle that has sub-items with Quantity components
    bundleHasQuantityOrOptional = (item: ISalesItem): boolean => {
        let productInfo = this.getProductInfoForSalesItem(item);
        if (productInfo === undefined)
            return false;
        return productInfo.components?.some((c: ProductComponent) => c.componentType === ComponentType.Quantity || c.componentType === ComponentType.Optional);
    }

    // Determine if this is a product that cannot have it's price altered
    priceIsLocked = (item: ISalesItem): boolean => {
        let productInfo = this.getProductInfoForSalesItem(item);
        if (productInfo === undefined)
            return false;
        return productInfo.properties?.some((pp: ProductProperty) => pp.key === ProductPropertyKeys.PriceLocked);
    }

    // Get the components within a bundle
    getSubComponents = (item: ISalesItem): ProductComponent[] => {
        let productInfo = this.getProductInfoForSalesItem(item);
        if (productInfo === undefined)
            return new Array<ProductComponent>();
        return productInfo.components;
    }

    // Get the component type of a sub-item within a bundle
    getComponentType = (salesItem: ISalesItem, subItem: ISalesItem): ComponentType => {
        let components = this.getSubComponents(salesItem);
        let thisItem = components.find(c => c.productNumber === (subItem.productNumber?.toString() ?? ""));
        return thisItem?.componentType ?? ComponentType.Required;
    }

    getSimpleSalesItem = (productNumber: string): SalesItem => {
        let salesItem = new SalesItem(undefined, this.props.salesBase);
        salesItem.productNumber = new ItemNumber(productNumber);
        salesItem.unitCode = this.props.salesBase?.pricing?.priceList?.prices.find((p: Price) => p.productNumber === productNumber)?.unitCode ?? "Each";
        return salesItem;
    }


    render() {
        let salesOrder = this.props.salesBase;
        let childrenCustomers = this.props.childrenCustomers;
        return <div>
                   <h3>Products</h3>
                   <table className="products">
                       <thead>
                       <tr>
                           <th className="item-product">Product</th>
                           {Object.keys(this.props.childrenCustomers).length > 0 && <th className="item-school">School</th>}
                           <th className="item-quantity">Qty</th>
                           <th className="item-price">Price</th>
                           <th className="item-total">Amount</th>
                       </tr>
                       </thead>
                       <tbody>
                       </tbody>
                   </table>
                   {salesOrder.items != null &&
                       // Show only products without a parent item ID in the main list, plus a blank at the end
                       this.getMainLineItemsPlusBlank().map((item: SalesItem, index: number) =>
                           <CollapseCtl key={index} itemId={item.itemId} hidden={!this.isBundle(item)} isOpen={item.showBundle} mainLine={
                               <ProductsQtyCtl productSelectItems={salesOrder.pricing?.priceList?.prices.map((p: Price) => {
                                       return { name: p.product?.title + " (" + p.unitCode + ")", value: p.productNumber, data: { amount: p.amountOrPercent.toString(), unitCode: p.unitCode } }
                                   })}
                                   hideCustomerDropDown={Object.entries(childrenCustomers).length === 0 || item.productNumber?.toString() === Constants.NEW_PRODUCT_LINE || this.bundleHasQuantityOrOptional(item)}
                                   customerList={Object.entries(childrenCustomers).map(c => {
                                        return { name: c["1"], value: c["0"] }
                                   })}
                                   item={item} salesBase={this.props.salesBase} disabled={this.props.disabled}
                                   onChange={this.onProductQtyPriceChange} deleteItem={this.deleteLineItem}
                                   isLocked={!item.overridePricing} showLock={!this.priceIsLocked(item)}
                                   hidePrice={item.productNumber?.toString() === Constants.NEW_PRODUCT_LINE}
                                   hideQtyEdit={item.productNumber?.toString() === Constants.NEW_PRODUCT_LINE}
                                   hideDelete={item.productNumber?.toString() === Constants.NEW_PRODUCT_LINE} />
                           } onChange={this.onBundleChange}>
                               {// In the collapsible area, show the bundle sub-items that are quantity components and have been added to the order
                                   this.getSubLineItemsPlusBlank(item).map((subItem: SalesItem, index: number) =>
                                       <ProductsQtyCtl key={index} productSelectItems={this.getSubComponents(item).map((c: ProductComponent) => {
                                               return { name: c.product?.title + " (" + c.unitCode + ")", value: c.productNumber }
                                           })}
                                           hideCustomerDropDown={Object.entries(childrenCustomers).length === 0 || subItem.productNumber?.toString() === Constants.NEW_PRODUCT_LINE ||
                                                                 this.getComponentType(item, subItem) === ComponentType.Optional}
                                           customerList={Object.entries(childrenCustomers).map(c => {
                                               return { name: c["1"], value: c["0"] }
                                           })}
                                           item={subItem} salesBase={this.props.salesBase} disabled={this.props.disabled}
                                           onChange={this.onProductQtyPriceChange} deleteItem={this.deleteLineItem}
                                           hidePriceDropDown={!(subItem.productNumber?.toString() === Constants.NEW_PRODUCT_LINE || this.getComponentType(item, subItem) !== ComponentType.Required)}
                                           hidePrice={true}
                                           hideDelete={!(subItem.productNumber?.toString() !== Constants.NEW_PRODUCT_LINE && this.getComponentType(item, subItem) === ComponentType.Quantity)}
                                           hideQtyEdit={item.productNumber?.toString() === Constants.NEW_PRODUCT_LINE || this.getComponentType(item, subItem) !== ComponentType.Quantity} />)
                               }
                               {// In the collapsible area, show the bundle sub-items that are required and not added to the order
                                    this.getSubComponents(item).filter((pc: ProductComponent) => pc.componentType === ComponentType.Required).map((subItem: ProductComponent) =>
                                        <ProductsQtyCtl item={this.getSimpleSalesItem(subItem.productNumber)} disabled={this.props.disabled}
                                            hideCustomerDropDown={true} hidePriceDropDown={true} hidePrice={true} hideDelete={true} hideQtyEdit={true} />)
                                }
                           </CollapseCtl>)
                        }
               </div>;
    }
}
